/**
 * Line renderer (blob `renderLine`). Dedupe + gap injection,
 * dual-Y, log scale, points, dashed fit overlays, filter thresholds, leg +
 * changeover band hooks, tooltip + drag-zoom hooks. Reads presentation from the
 * `plotState` style; data from the `/plot` response.
 */

import type uPlot from 'uplot';
import { plotState } from '../../../state/plot-state.ts';
import { buildSeriesColorer, fitColorFor } from '../plot-constants.ts';
import { plotFocusShip } from '../plot-ui-state.ts';
import { themeColors, chartContainerSize } from './chart-dom.ts';
import {
  dedupeByX,
  computeGapThreshold,
  injectGaps,
  bridgeOwnGaps,
  collectThresholds,
  applyAxisBounds,
  buildLegendOpts,
  buildXAxis,
  buildYAxis,
  yAxisTitle,
  utcTzDate,
  rebuildUplot,
  applyHoverFocus,
} from './uplot-helpers.ts';
import {
  drawColorLegendBox,
  drawLegBands,
  drawChangeoverBands,
  drawRegisteredOverlays,
  drawThresholds,
} from './overlays.ts';
import { collectOverlayShapes, type OverlayContext } from '../../../state/chart-overlays.ts';
import { plottedShipIds } from '../../../state/store.ts';
import {
  colorPlanFor,
  drawColoredLineSegments,
  planColors,
  pointBuckets,
} from './color-plan.ts';
import { colorScaleGradientCss } from '../plot-constants.ts';
import { colorLegend, plottedSource } from '../plot-ui-state.ts';
import { sourceHasHistory } from '../../../state/data-sources.ts';
import { drawLiveOverlay, reserveLiveRoom, type TagSeries } from './last-value-tags.ts';
import { updateTooltip } from './tooltip.ts';
import { onXScaleChanged } from '../zoom.ts';
import { runtime } from '../plot-runtime.ts';
import type { PlotResponse } from '../../../api/types.ts';

type NumArr = Array<number | null>;

/**
 * What a chart tells an overlay provider about itself.
 *
 * Facts only, and only facts already on screen: which entities are drawn, which
 * columns on which axis, and the x window as drawn *now* - so a provider that
 * answers per window is asked again after a zoom without the plot arranging it.
 * Nothing here lets a provider reach back into the chart.
 */
function overlayContext(u: uPlot, data: PlotResponse): OverlayContext {
  const columnsOn = (axis: 'left' | 'right'): string[] =>
    data.series.filter((s) => s.axis === axis).map((s) => s.column ?? s.name);
  const [xFrom, xTo] = u.scales.x?.min != null && u.scales.x?.max != null
    ? [u.scales.x.min, u.scales.x.max]
    : [data.first_x ?? 0, data.last_x ?? 0];
  return {
    plotType: 'line',
    entityIds: plottedShipIds(),
    left: columnsOn('left'),
    right: columnsOn('right'),
    xIsTime: !!data.x_is_time,
    xFrom,
    xTo,
  };
}

export function renderLine(data: PlotResponse): void {
  const c = themeColors();
  const s = plotState.value;
  const style = s.style;
  const hasRight = data.series.some((ser) => ser.axis === 'right');

  const xs = data.x.slice();

  // Fit series share the plotted (downsampled) x, so they flow through the same
  // dedupe + gap pipeline as the main series.
  const fitEntries = data.fits.filter(
    (f) => !f.error && Array.isArray(f.y) && f.y.length === xs.length,
  );
  /*
   * Colour by a column: the line itself changes colour along its length.
   *
   * The plan (which value is which colour) is built from the whole response,
   * then reduced to one *bucket index per point* - because those indices have
   * to survive `dedupeByX` and `injectGaps` alongside the y values, and an
   * index is a number, which those two copy about happily. A raw categorical
   * string would not be. `plan` is null whenever nothing is being coloured, and
   * everything below then behaves exactly as it did before.
   */
  const colorScaleName = s.color_scale || 'Viridis';
  const colorPlan =
    data.color_by && data.color_values?.length ? colorPlanFor([{ data }], colorScaleName) : null;
  const colorColors = colorPlan ? planColors(colorPlan, colorScaleName) : [];

  const values: NumArr[] = [
    ...data.series.map((ser) => ser.values.slice()),
    ...fitEntries.map((f) => f.y!.slice()),
    ...(colorPlan ? [pointBuckets(data.color_values!, colorPlan)] : []),
  ];
  const sanitized = dedupeByX(xs, values);

  const gapThreshold = style.connect_gaps ? null : computeGapThreshold(sanitized.x);
  const gapped = injectGaps(sanitized.x, sanitized.series, gapThreshold);
  // Popped back off now that it has been through the same transforms; what is
  // left in `gapped.series` is the series list the rest of this function expects.
  const colorBuckets = colorPlan ? gapped.series[gapped.series.length - 1]! : null;

  // Multi-ship draws allocate colour per the chosen mode; a single ship keeps
  // the existing per-signal palette untouched.
  const colorOf = buildSeriesColorer(s, data.series, plotFocusShip.value);

  /*
   * On a multi-ship overlay every series is null wherever *another* ship
   * sampled, because the merged axis carries all their timestamps. Those nulls
   * are structural, not missing data - with `spanGaps` off uPlot breaks the
   * path at each one and a two-ship line chart draws no segments at all.
   *
   * So a multi-ship overlay spans them. The cost is that a real outage no
   * longer shows as a break in the line; the per-ship "data only available for
   * N of M months" warning still reports it.
   */
  const multiShip = data.series.some((ser) => ser.ship);
  const spanGaps = !!style.connect_gaps || multiShip;
  // Live merges tables written at different rates; each line joins its own
  // points across the others' rows (see `bridgeOwnGaps`).
  if (!spanGaps && !sourceHasHistory(plottedSource.value)) {
    const n = data.series.length;
    gapped.series.splice(0, n, ...bridgeOwnGaps(gapped.x, gapped.series.slice(0, n)));
  }

  const seriesOpts: uPlot.Series[] = [{}];
  const uData: uPlot.AlignedData = [gapped.x as number[]];
  const seriesScales: string[] = ['x'];
  data.series.forEach((ser, i) => {
    const color = colorOf(ser, i);
    const scale = ser.axis === 'right' && hasRight ? 'yR' : 'y';
    const opt: uPlot.Series = {
      label: ser.name,
      stroke: color,
      width: style.line_width,
      scale,
      spanGaps,
      points: {
        show: !!style.show_points,
        size: Math.max(4, style.line_width + 3),
        stroke: color,
        fill: color,
      },
    };
    // uPlot's own path is one colour per series; the hook below strokes the
    // segments instead, so it must not also draw underneath them.
    if (colorBuckets) opt.paths = () => null;
    seriesOpts.push(opt);
    seriesScales.push(scale);
    uData.push(gapped.series[i] as NumArr);
  });

  // Fit overlays: dashed, drawn after main series so they sit on top.
  const nMain = data.series.length;
  fitEntries.forEach((f, fi) => {
    const baseIdx = data.series.findIndex((ser) => ser.name === f.series);
    const baseSeries = baseIdx >= 0 ? data.series[baseIdx] : null;
    const color =
      baseIdx >= 0 ? (baseSeries ? colorOf(baseSeries, baseIdx) : fitColorFor(s, f.series, baseIdx)) : '#888';
    const scale = baseSeries && baseSeries.axis === 'right' && hasRight ? 'yR' : 'y';
    seriesScales.push(scale);
    seriesOpts.push({
      label: `${f.series} · fit`,
      stroke: color,
      width: Math.max(2.25, style.line_width + 1),
      dash: [7, 4],
      scale,
      spanGaps: true,
      points: { show: false },
    });
    uData.push(gapped.series[nMain + fi] as NumArr);
  });

  const thresholds = style.show_thresholds ? collectThresholds(data) : [];
  // A live chart says its newest reading at the end of each line (fits excluded).
  const tagSeries: TagSeries[] = sourceHasHistory(plottedSource.value)
    ? []
    : data.series.map((ser, i) => ({ idx: i + 1, color: colorOf(ser, i) }));

  // A discrete scale names its values, so it is drawn on the canvas where the
  // colours are; a gradient is a continuum and reads better as the strip under
  // the chart. Never both, and never a leftover from the last plot.
  const discreteLegend = colorPlan?.kind === 'discrete' ? colorPlan.legend : null;
  if (colorPlan?.kind === 'numeric') {
    colorLegend.set({
      colorBy: colorPlan.colorBy,
      min: Number(colorPlan.domain.min.toPrecision(4)),
      max: Number(colorPlan.domain.max.toPrecision(4)),
      gradientCss: colorScaleGradientCss(s.color_scale),
    });
  } else {
    colorLegend.set(null);
  }

  const xLabel = data.x_column || (data.x_is_time ? 'Time' : 'x');
  const leftLabel = yAxisTitle(data.series, 'left', s.axis_titles.y);
  const rightLabel = yAxisTitle(data.series, 'right', s.axis_titles.yr);

  const axes: uPlot.Axis[] = [
    buildXAxis(c, !!data.x_is_time, xLabel),
    buildYAxis(c, 'y', 'left', leftLabel),
  ];
  if (hasRight) axes.push(buildYAxis(c, 'yR', 'right', rightLabel));

  const useLog = !!style.log_scale;
  const logCfg = useLog ? { distr: 3 as const, log: 10 as const } : {};
  const scales: Record<string, uPlot.Scale> = {
    x: { time: !!data.x_is_time },
    y: { ...logCfg },
  };
  if (hasRight) scales.yR = { ...logCfg };
  applyAxisBounds(scales, !!data.x_is_time, hasRight);

  const opts: uPlot.Options = {
    ...chartContainerSize(),
    scales,
    axes,
    series: seriesOpts,
    cursor: {
      x: true,
      y: true,
      drag: { x: true, y: false, setScale: true },
    },
    legend: buildLegendOpts(data.series.length),
    hooks: {
      drawClear: [
        (u): void => {
          if (s.show_legs && data.x_is_time && runtime.lastLegs?.length) {
            drawLegBands(u, runtime.lastLegs);
          }
          if (s.show_changeovers && data.x_is_time && runtime.lastChangeovers?.length) {
            drawChangeoverBands(u, runtime.lastChangeovers);
          }
        },
      ],
      draw: [
        (u): void => {
          if (colorBuckets) {
            for (let i = 1; i <= data.series.length; i++) {
              drawColoredLineSegments(
                u,
                i,
                uData,
                seriesScales[i]!,
                colorBuckets,
                colorColors,
                style.line_width,
                spanGaps,
              );
            }
            if (discreteLegend) {
              drawColorLegendBox(u, { title: data.color_by!, items: discreteLegend });
            }
          }
          if (thresholds.length) drawThresholds(u, thresholds);
          if (tagSeries.length) {
            drawLiveOverlay(u, tagSeries, {
              xIsTime: !!data.x_is_time,
              layout: style.live_layout,
              nowLabel: style.live_now_label,
              nowS: runtime.liveNowS,
            });
          }
          // Last, and on top of everything the chart draws for itself: a guest
          // never hides the data somebody came to look at. Collected per draw
          // rather than per render, so a provider that learns something after
          // the chart was built shows it on the next redraw without the plot
          // knowing anything happened. With nothing registered this is one
          // `Map.size` check.
          drawRegisteredOverlays(u, collectOverlayShapes(overlayContext(u, data)));
        },
      ],
      setCursor: [(u): void => updateTooltip(u, data, seriesOpts)],
      setScale: [
        (u, key): void => {
          if (key === 'x') onXScaleChanged(u, !!data.x_is_time);
        },
      ],
    },
  };

  if (data.x_is_time) opts.tzDate = utcTzDate;
  if (tagSeries.length) reserveLiveRoom(opts, style.live_layout, style.live_now_label);
  applyHoverFocus(opts);
  rebuildUplot(uData, opts);
}
